import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Datastore } from '@freelancer/datastore';
import type {
  PoolModel,
  PoolsCollection,
  Skill,
  SkillRank,
  ThreadsCollection,
} from '@freelancer/datastore/collections';
import {
  FreelancerReputation,
  SearchUser,
} from '@freelancer/datastore/collections';
import { Feature, FeatureFlagsService } from '@freelancer/feature-flags';
import { Tracking } from '@freelancer/tracking';
import { ModalService, trackByValue } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HighlightBackground } from '@freelancer/ui/highlight-text';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ModalColor, ModalSize } from '@freelancer/ui/modal';
import { OnlineIndicatorSize } from '@freelancer/ui/online-indicator';
import {
  FontColor,
  FontType,
  FontWeight,
  ReadMore,
  TextSize,
} from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import type { UsernameBadges } from '@freelancer/ui/username';
import { UsernameSize } from '@freelancer/ui/username';
import { isArray, isDefined } from '@freelancer/utils';
import { ThreadTypeApi } from 'api-typings/messages/messages_types';
import { CorporateStatusApi } from 'api-typings/users/users';
import escapeStringRegexp from 'escape-string-regexp';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'fl-user-search-card-action',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSearchCardActionComponent {}

@Component({
  selector: 'fl-user-search-card',
  template: `
    <div
      class="UserAvatar"
      [flShowDesktop]="true"
      [flMarginRight]="'small'"
    >
      <fl-user-avatar
        [isOnline]="isLoggedIn ? user.isOnline : undefined"
        [onlineIndicatorSize]="OnlineIndicatorSize.XLARGE"
        [size]="AvatarSize.MAX"
        [users]="[user]"
      ></fl-user-avatar>
    </div>

    <div class="UserCardContent">
      <div
        class="UserCardHeader"
        [flMarginBottom]="'xxxsmall'"
      >
        <fl-user-avatar
          class="UserCardHeader-avatar"
          [flHideDesktop]="true"
          [flMarginRight]="'small'"
          [isOnline]="isLoggedIn ? user.isOnline : undefined"
          [size]="AvatarSize.MAX"
          [users]="[user]"
          [onlineIndicatorSizeDesktop]="OnlineIndicatorSize.XLARGE"
          [onlineIndicatorSizeTablet]="OnlineIndicatorSize.LARGE"
          [onlineIndicatorSize]="OnlineIndicatorSize.LARGE"
        ></fl-user-avatar>

        <div class="LeftColDesktop">
          <fl-user-info
            class="UserInfo"
            [flMarginBottom]="showSkillRank ? 'xxxsmall' : 'none'"
            [freelancerReputation]="reputation"
            [isDeloitteProject]="!showEarningScore"
            [ratingControl]="ratingControl"
            [showNewFreelancerLabel]="showNewFreelancerLabel$ | flAsync"
            [showTooltip]="showTooltip"
            [user]="user"
            [userBadges]="userBadges"
            [usernameSize]="UsernameSize.LARGE"
            [usernameSizeDesktop]="UsernameSize.XLARGE"
          >
            <fl-user-info-my-list>
              <ng-content select="fl-user-search-card-action"></ng-content>
            </fl-user-info-my-list>
          </fl-user-info>
        </div>
        <div
          *ngIf="user.hourlyRate && !isInsource"
          class="RateContainer"
          [flShowDesktop]="true"
        >
          <ng-container *ngTemplateOutlet="headerInfoTemplate"></ng-container>
        </div>
      </div>
      <ng-template #headerInfoTemplate>
        <p
          class="text-mid font-bold"
          [flMarginRight]="'xxsmall'"
          [flMarginRightDesktop]="'none'"
        >
          {{ user.hourlyRate | flCurrency: 'USD' : { withPrecision: false } }}
        </p>
        <p
          class="text-xsmall Time"
          i18n="Freelancer rate per hour"
          [flMarginRight]="'xxsmall'"
          [flMarginRightDesktop]="'none'"
          [flMarginBottomDesktop]="'xxsmall'"
        >
          per hour
        </p>
      </ng-template>
      <div
        *ngIf="user.hourlyRate && !isInsource"
        class="RateContainer"
        [flMarginBottom]="'xxxsmall'"
        [flHideDesktop]="true"
      >
        <ng-container *ngTemplateOutlet="headerInfoTemplate"></ng-container>
      </div>

      <div class="UserCardFooter">
        <div
          class="LeftColDesktop LeftColDesktop-footer"
          [class.LeftColDesktop-chat]="showChatButton$ | flAsync"
          [class.LeftColDesktop-requestQuote]="showContact"
        >
          <div
            *ngIf="showSkillRank && (skillRank || atnPoolName)"
            class="SkillRank"
            [flMarginBottom]="'xsmall'"
          >
            <ng-container *ngIf="skillRank">
              <span
                class="text-color-inherit text-xsmall"
                i18n="User's skill rank"
                [class.SkillRank-item]="!!atnPoolName"
              >
                #{{ skillRank.rank }}
                in
                {{ skillRank.skillName }}
              </span>
            </ng-container>
            <span
              class="text-color-inherit text-xsmall"
              *ngIf="atnPoolName"
            >
              {{ atnPoolName }}
            </span>
          </div>

          <p
            class="font-bold"
            *ngIf="!showSkillRank"
            i18n="User's tagline"
            [flMarginBottom]="'xxxsmall'"
          >
            {{ user.tagLine }}
          </p>

          <fl-text
            [displayLineBreaks]="displayLineBreaks"
            [flMarginBottom]="'xxxsmall'"
            [flMarginRight]="
              (showHireMe$ | flAsync) && !isInsource && showContact
                ? 'xsmall'
                : undefined
            "
            [fontType]="FontType.PARAGRAPH"
            [maxLines]="showSkillRank ? 3 : 2"
            [readMore]="ReadMore.LINK"
            (expand)="handleExpand($event)"
          >
            <ng-container
              *ngIf="
                shouldHighlightKeywords &&
                  searchTerm &&
                  user.profileDescription;
                else defaultDescription
              "
            >
              <fl-highlight-text
                [backgroundColor]="HighlightBackground.INHERIT"
                [highlight]="searchTerm"
                [text]="user.profileDescription"
              ></fl-highlight-text>
            </ng-container>

            <ng-template #defaultDescription>
              <ng-container *ngIf="!showSkillRank; else newDescription">
                {{ user.profileDescription }}
              </ng-container>
            </ng-template>

            <ng-template #newDescription>
              <ng-container *ngIf="!!user.tagLine">
                <strong class="Tagline"> {{ user.tagLine }}</strong
                >{{ user.profileDescription }}
              </ng-container>
            </ng-template>
          </fl-text>

          <div
            [flMarginBottom]="'xsmall'"
            [flMarginBottomDesktop]="'none'"
          >
            <fl-tag
              *ngFor="
                let skill of userRelevantSkillNames | slice: 0 : displayLimit;
                trackBy: trackByValue
              "
              [borderless]="true"
            >
              {{ skill }}
            </fl-tag>
          </div>
        </div>
        <div
          [class.Buttons]="!isInsource"
          [class.ButtonsInsource]="isInsource"
          [class.Buttons-chat]="
            !isInsource && ((showChatButton$ | flAsync) || showQuoteButton)
          "
          [class.ButtonsInsource-chat]="
            isInsource && ((showChatButton$ | flAsync) || showQuoteButton)
          "
          [class.Buttons-requestQuote]="!isInsource && showContact"
        >
          <fl-chat-button
            *ngIf="showChatButton$ | flAsync"
            class="Chat"
            [flMarginRight]="'xsmall'"
            [otherMemberIds]="[user.id]"
            [fullWidth]="true"
            [threadType]="ThreadTypeApi.PRIVATE_CHAT"
            (click)="handleButtonClick($event, 'SearchChatButton')"
          ></fl-chat-button>

          <app-direct-quote-button
            *ngIf="showQuoteButton"
            class="QuoteButton"
            [user]="user"
            (click)="handleButtonClick($event, 'QuoteButton')"
          ></app-direct-quote-button>

          <ng-container
            *ngIf="userHasBeenInvited !== undefined; else showDefaultButton"
          >
            <ng-content select="app-search-invite-to-bid-button"></ng-content>
          </ng-container>

          <ng-template #ctaButton>
            <ng-container
              *ngIf="isInsource"
              i18n="Offer Project Button"
            >
              Offer Project
            </ng-container>
            <ng-container *ngIf="!isInsource">
              <ng-container
                *ngIf="showContact"
                i18n="Contact Button"
              >
                Contact
              </ng-container>
              <ng-container
                *ngIf="!showContact"
                i18n="Hire Freelancer Button"
              >
                Hire
              </ng-container>
            </ng-container>
          </ng-template>

          <ng-template #showDefaultButton>
            <ng-container *ngIf="showHireMe$ | flAsync">
              <fl-button
                *ngIf="!isInsource && showContact"
                class="InviteToBidButton"
                i18n="Invite to bid button"
                [color]="ButtonColor.DEFAULT"
                [flMarginRight]="'xxsmall'"
                [size]="ButtonSize.SMALL"
                (click)="handleInviteButtonClick($event)"
              >
                Invite to Bid
              </fl-button>
              <fl-button
                *ngIf="showHireButton"
                class="Cta"
                [class.Cta-chat]="
                  (showChatButton$ | flAsync) || showQuoteButton
                "
                [color]="ButtonColor.SECONDARY"
                [size]="ButtonSize.SMALL"
                (click)="handleMainCtaClick($event)"
              >
                <ng-container *ngTemplateOutlet="ctaButton"></ng-container>
              </fl-button>
            </ng-container>
          </ng-template>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./user-search-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSearchCardComponent implements OnChanges, OnInit {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  FontType = FontType;
  FontWeight = FontWeight;
  HighlightBackground = HighlightBackground;
  IconColor = IconColor;
  IconSize = IconSize;
  ModalColor = ModalColor;
  ModalSize = ModalSize;
  OnlineIndicatorSize = OnlineIndicatorSize;
  ReadMore = ReadMore;
  TextSize = TextSize;
  ThreadTypeApi = ThreadTypeApi;
  trackByValue = trackByValue;
  UsernameSize = UsernameSize;

  @Input() authId: number | undefined;
  @Input() isInsource = false;
  @Input() isLoggedIn: boolean;

  /*
   * Individual terms from this string are highlighted in the profile
   * description if shouldHighlightKeywords is true. In addition to skillsFilterNames,
   * is also used to determine the user's top relevant skill names to show in the card
   */
  @Input() searchTerm?: string;

  /*
   * to highlight terms from searchTerm on the profile
   * description based on the matching logic in highlight-text
   */
  @Input() shouldHighlightKeywords?: boolean;

  /*
   * Display the chat button alongside the Hire Me button
   * Uses the most recent thread between the two users, otherwise
   * creating a new thread. Does not guarantee that a conversation
   * can be started, if the user lacks the right context any
   * sent messages will be rejected due to insufficient permissions
   */
  @Input() showChatButton = false;

  /*
   * Only to be used in Deloitte Lists context
   */
  @Input() showEarningScore = true;

  @Input() showQuoteButton = false;

  @Input() showHireButton = true;

  /**
   * Replaces the Hire Me button with Contact button.
   */
  @Input() showContact = false;

  @Input() skillsFilter?: readonly Skill[];

  /* Is used to determine the user's top relevant skill names to show in the card */
  @Input() skillsFilterNames?: readonly string[];
  @Input() trackingSection?: string;
  @Input() user: SearchUser;
  @Input() reputation?: FreelancerReputation;
  @Input() userHasBeenInvited?: boolean;
  @Input() showSkillRank = false;
  @Input() showTooltip = true;

  showNewFreelancerLabel$ = of(true);
  atnPoolName?: string;
  displayLineBreaks = false;
  ratingControl: FormControl<number>;
  skillRank?: SkillRank;
  userBadges: UsernameBadges;
  showHireMe$: Observable<boolean>;

  userRelevantSkillNames: readonly string[];

  showChatButton$: Observable<boolean>;

  readonly displayLimit = 3;
  readonly MAXIMUM_USER_SEARCH_CARD_SKILLS_LENGTH = 3;

  constructor(
    @Inject(UI_CONFIG) public uiConfig: UiConfig,
    private datastore: Datastore,
    private modalService: ModalService,
    private router: Router,
    private tracking: Tracking,
    private featureFlag: FeatureFlagsService,
  ) {}

  ngOnInit(): void {
    this.userBadges = {
      verified: this.user.status.freelancerVerifiedUser,
      corporate:
        this.user.corporate &&
        this.user.corporate.status === CorporateStatusApi.ACTIVE,
      preferred_freelancer: this.user.preferredFreelancer,
      rising_star: this.user.isRisingStar,
    };

    this.ratingControl = new FormControl(this.user.rating, {
      nonNullable: true,
    });

    const threadsCollection = this.datastore.collection<ThreadsCollection>(
      'threads',
      query =>
        this.isLoggedIn && this.authId !== this.user.id && this.showChatButton
          ? query
              .where('otherMembers', 'equalsIgnoreOrder', [this.user.id])
              .where('threadType', '==', ThreadTypeApi.PRIVATE_CHAT)
          : of(query.null()),
    );

    /**
     * The chat button will determine which thread is used, it is sufficient
     * here just to check that at least one exists
     */
    this.showChatButton$ = threadsCollection
      .valueChanges()
      .pipe(map(threads => threads.length > 0));

    this.showNewFreelancerLabel$ = this.datastore
      .collection<PoolsCollection>('pools', query =>
        query.where('isTalentNetwork', '==', true),
      )
      .valueChanges()
      .pipe(map(talentNetworkPools => talentNetworkPools.length === 0));

    this.showHireMe$ = this.featureFlag.getFlag(Feature.HIRE_ME);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'searchTerm' in changes ||
      'skillsFilterNames' in changes ||
      'user' in changes
    ) {
      if (isDefined(this.user)) {
        const keyword = this.searchTerm?.trim().toLowerCase() || '';
        const userSkillNames = this.user.skills.map(skill => skill.name);

        this.userRelevantSkillNames = isArray(this.skillsFilterNames)
          ? this.getMostRelevantSkillNamesBasedOnKeywordAndSkillsFilter(
              keyword,
              userSkillNames,
              this.skillsFilterNames,
              this.MAXIMUM_USER_SEARCH_CARD_SKILLS_LENGTH,
            )
          : userSkillNames;
      }
    }

    // Show user's skill rank and ATN based on the keyword and skills filter
    if (
      'searchTerm' in changes ||
      'skillsFilter' in changes ||
      'user' in changes
    ) {
      // Set `this.skillRank` based on the user's skill ranks, keyword filter, and skills filter

      // User has no rank in any skill
      if (!isDefined(this.user.skillRanks)) {
        this.skillRank = undefined;
      }

      // No search term and no skill filter is applied
      else if (!this.searchTerm && !this.skillsFilter?.length) {
        // Choose the user's highest skill rank
        this.skillRank = this.getHighestRankingSkillRank(this.user.skillRanks);
      }

      // Get the user's highest ranking skill that matches the keyword or skill filter
      else {
        this.skillRank = this.getSkillRankBasedOnFilters(
          this.user.skillRanks,
          this.searchTerm?.trim().toLowerCase() || '',
          this.skillsFilter ?? [],
        );
      }

      // Set `this.atnPool` based on `this.skillRank`, keyword filter, and skills filter

      // User has no ATN pools
      if (!this.user.atnPools?.length) {
        this.atnPoolName = undefined;
      }

      // Get ATN associated with `this.skillRank`
      else if (isDefined(this.skillRank)) {
        this.atnPoolName = this.skillRank.atnPoolName ?? '';
      }

      // No search term and no skill filter is applied
      else if (!this.searchTerm && !this.skillsFilter?.length) {
        // Get the first from the user's list of ATNs
        this.atnPoolName = this.user.atnPools[0].displayName;
      }

      // Get user's ATN associated with skill that matches the keyword or skill filter
      else {
        this.atnPoolName = this.getAtnBasedOnFilters(
          this.user.atnPools,
          this.searchTerm?.trim().toLowerCase() || '',
          this.skillsFilter ?? [],
        )?.displayName;
      }
    }
  }

  getAtnBasedOnFilters(
    atnPools: readonly PoolModel[],
    keyword: string,
    skillsFilter: readonly Skill[],
  ): PoolModel | undefined {
    const atnMatchingKeywordFilter = this.getAtnBasedOnSearchTerm(
      atnPools,
      keyword,
    );

    const atnMatchingSkillsFilter = this.getAtnBasedOnSkillsFilter(
      atnPools,
      skillsFilter,
    );

    return isDefined(atnMatchingKeywordFilter)
      ? atnMatchingKeywordFilter
      : atnMatchingSkillsFilter;
  }

  getAtnBasedOnSearchTerm(
    atnPools: readonly PoolModel[],
    keyword: string,
  ): PoolModel | undefined {
    // User has no ATNs or keyword is not applied
    if (!atnPools.length || !this.searchTerm) {
      return undefined;
    }

    return atnPools.find(atn =>
      atn.skills?.find(skill => skill.name.toLowerCase() === keyword),
    );
  }

  getAtnBasedOnSkillsFilter(
    atnPools: readonly PoolModel[],
    skillsFilter: readonly Skill[],
  ): PoolModel | undefined {
    // User has no ATN or skills filter is not applied
    if (!atnPools.length || !this.skillsFilter?.length) {
      return undefined;
    }

    // Single skill filter is applied
    if (this.skillsFilter.length === 1) {
      const singleSkillsFilter = skillsFilter[0];

      return atnPools.find(atn =>
        atn.skills?.find(skill => skill.id === singleSkillsFilter.id),
      );
    }

    // Multiple skills filter is applied
    if (this.skillsFilter.length > 1) {
      const skillsFilterIds = this.skillsFilter?.map(
        skillFilter => skillFilter.id,
      );

      return atnPools.find(atn =>
        atn.skills?.find(skill => skillsFilterIds.includes(skill.id)),
      );
    }
  }

  getSkillRankBasedOnFilters(
    skillRanks: readonly SkillRank[],
    keyword: string,
    skillsFilter: readonly Skill[],
  ): SkillRank | undefined {
    const skillRankMatchingKeywordFilter = this.getSkillRankBasedOnSearchTerm(
      skillRanks,
      keyword,
    );

    const skillRankMatchingSkillsFilter = this.getSkillRankBasedOnSkillsFilter(
      skillRanks,
      skillsFilter,
    );

    if (!isDefined(skillRankMatchingKeywordFilter)) {
      return skillRankMatchingSkillsFilter;
    }

    if (!isDefined(skillRankMatchingSkillsFilter)) {
      return skillRankMatchingKeywordFilter;
    }

    return skillRankMatchingKeywordFilter.rank <=
      skillRankMatchingSkillsFilter.rank
      ? skillRankMatchingKeywordFilter
      : skillRankMatchingSkillsFilter;
  }

  getSkillRankBasedOnSearchTerm(
    skillRanks: readonly SkillRank[],
    keyword: string,
  ): SkillRank | undefined {
    // User has no rank in any skill or keyword is not applied
    if (!skillRanks.length || !this.searchTerm) {
      return undefined;
    }

    return skillRanks.find(
      skillRank => skillRank.skillName.toLowerCase() === keyword, // Exact match
    );
  }

  getSkillRankBasedOnSkillsFilter(
    skillRanks: readonly SkillRank[],
    skillsFilter: readonly Skill[],
  ): SkillRank | undefined {
    // User has no rank in any skill or skills filter is not applied
    if (!skillRanks.length || !this.skillsFilter?.length) {
      return undefined;
    }

    // Single skill filter is applied
    if (this.skillsFilter.length === 1) {
      const singleSkillsFilter = skillsFilter[0];

      return skillRanks.find(
        skillRank => skillRank.skillId === singleSkillsFilter.id,
      );
    }

    // Multiple skills filter is applied
    if (this.skillsFilter.length > 1) {
      const skillsFilterIds = this.skillsFilter?.map(
        skillFilter => skillFilter.id,
      );

      const skillRanksMatchingSkillFilter = skillRanks.filter(skillRank =>
        skillsFilterIds.includes(skillRank.skillId),
      );

      return this.getHighestRankingSkillRank(skillRanksMatchingSkillFilter);
    }
  }

  getHighestRankingSkillRank(
    skillRanks: readonly SkillRank[],
  ): SkillRank | undefined {
    return skillRanks.length > 0
      ? skillRanks.reduce((previous, current) =>
          previous.rank < current.rank ? previous : current,
        )
      : undefined;
  }

  getMostRelevantSkillNamesBasedOnKeywordAndSkillsFilter(
    keyword: string,
    skillNames: readonly string[],
    skillsFilterNames: readonly string[],
    skillCount: number,
  ): readonly string[] {
    const skillNamesMatchingExactKeywordOrAnySkill =
      keyword !== '' || skillsFilterNames.length > 0
        ? skillNames.filter(
            skillName =>
              skillName.trim().toLowerCase() === keyword ||
              skillsFilterNames.includes(skillName),
          )
        : [];

    const individualSearchTermsRegexMatch =
      this.getMatchRegexBasedOnKeyword(keyword);

    const skillNamesMatchingSomeTerms =
      keyword !== '' &&
      skillNamesMatchingExactKeywordOrAnySkill.length < skillCount
        ? skillNames.filter(
            skillName =>
              !skillNamesMatchingExactKeywordOrAnySkill.includes(skillName) &&
              skillName.match(individualSearchTermsRegexMatch),
          )
        : [];

    const relevantSkillNames = [
      ...skillNamesMatchingExactKeywordOrAnySkill,
      ...skillNamesMatchingSomeTerms,
    ];

    const skillNamesNotMatchingAnyFilter =
      relevantSkillNames.length > 0 && relevantSkillNames.length < skillCount
        ? skillNames.filter(
            skillName => !relevantSkillNames.includes(skillName),
          )
        : relevantSkillNames.length >= skillCount
          ? []
          : skillNames;

    return [...relevantSkillNames, ...skillNamesNotMatchingAnyFilter].slice(
      0,
      skillCount,
    );
  }

  getMatchRegexBasedOnKeyword(keyword: string): RegExp {
    const matchingSearchTerms = escapeStringRegexp(keyword.trim()).split(/\s+/);
    const regexString = `(${matchingSearchTerms
      .map(term => `\\b${term}`)
      .join('|')})`;

    return new RegExp(regexString, 'i');
  }

  handleButtonClick(event: MouseEvent, label: string): void {
    // importing TrackingModule in ComponentsModule for flTrackingLabel would build with a
    // missing Injection Token error for the search user card page in bits, so we inject
    // Tracking and manually create what would be the tracking event on button clicks here.
    this.tracking.track('user_action', {
      section: this.trackingSection ?? 'UserSearchCard',
      label,
      action: 'click',
      reference_id: this.user.id,
      reference_type: 'user_id',
    });
    event.stopPropagation();
    event.preventDefault();
  }

  handleInviteButtonClick(event: MouseEvent): void {
    this.handleButtonClick(event, 'InviteToBidButton');

    this.modalService.open('InviteModal', {
      size: ModalSize.SMALL,
      edgeToEdge: false,
      mobileFullscreen: true,
      inputs: {
        freelancerId: this.user.id,
        freelancerName: this.user.publicName,
      },
    });
  }

  handleMainCtaClick(event: MouseEvent): void {
    const label = this.isInsource
      ? 'OfferProjectButton'
      : this.showContact
        ? 'ContactButton'
        : 'HireMeButton';
    this.handleButtonClick(event, label);

    return this.isLoggedIn
      ? this.showContact
        ? this.handleContact()
        : this.openHireMeModal()
      : this.redirectToLogin();
  }

  handleExpand(isExpanded: boolean): void {
    this.displayLineBreaks = isExpanded;
  }

  openHireMeModal(): void {
    this.modalService.open('HireMeWebappModal', {
      inputs: {
        profileUserId: this.user.id,
        forceNonInsource: !this.isInsource,
      },
      color: ModalColor.DARK,
      size: this.user.isDeloitteDcUser ? ModalSize.MID : ModalSize.XSMALL,
      edgeToEdge: true,
      mobileFullscreen: true,
    });
  }

  handleContact(): void {
    this.modalService.open('UserProfileContactModal', {
      inputs: {
        profileUserId: this.user.id,
        profileUserDisplayName: this.user.publicName,
        contactOptionsTrackingSection: this.trackingSection ?? 'UserSearchCard',
      },
      size: ModalSize.MID,
      mobileFullscreen: true,
      keepModalInfo: true,
    });
  }

  redirectToLogin(): void {
    this.router.navigate(['/login'], {
      queryParams: { next: this.router.url },
    });
  }
}
